import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    { icon: 'ri-shield-check-line', text: 'The weight of life-safety responsibility' },
    { icon: 'ri-store-2-line', text: 'The reality of running a service-based business' },
    { icon: 'ri-price-tag-3-line', text: 'The frustration of competing on price' },
    { icon: 'ri-file-list-3-line', text: 'The importance of documentation and compliance' },
    { icon: 'ri-handshake-line', text: 'The value of long-term customer trust' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <img
                src="https://public.readdy.ai/ai/img_res/55f58ff0-c5af-4885-85a2-2e0c07bea16a.png"
                alt="Fireline Email Co."
                className="h-12 w-auto"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-400'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-red-600' : 'text-red-400'
                }`}
              >
                About
              </Link>
              <Link
                to="/"
                className="bg-red-600 text-white px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Book a Call
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative h-[420px] flex items-end bg-gray-900">
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-16 w-full">
          <p className="text-red-400 text-sm font-semibold uppercase tracking-widest mb-3">
            About Fireline Email Co.
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            The Man Behind<br />Fireline Email Co.
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-16">
          {/* Left column — portrait + signature */}
          <div className="md:col-span-1 flex flex-col items-center md:items-start">
            <div className="w-48 h-48 rounded-full overflow-hidden mb-6 ring-4 ring-red-100">
              <img
                src="https://public.readdy.ai/ai/img_res/35e4028a817152686ebb163e9f9746f6.jpg"
                alt="Kurt — Founder of Fireline Email Co."
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="text-2xl font-bold text-gray-900 font-serif italic">— Kurt</p>
            <p className="text-sm text-gray-500 mt-1">Founder, Fireline Email Co.</p>

            <div className="mt-8 bg-red-50 border border-red-100 rounded-lg p-5 w-full">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-widest mb-3">Background</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-500"><i className="ri-fire-line text-base" /></span>
                  Former paid on-call firefighter
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-500"><i className="ri-building-2-line text-base" /></span>
                  Commercial building owner
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-500"><i className="ri-briefcase-4-line text-base" /></span>
                  Small business operator
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-500"><i className="ri-mail-send-line text-base" /></span>
                  Email strategist
                </li>
              </ul>
            </div>
          </div>

          {/* Right column — story */}
          <div className="md:col-span-2 space-y-6 text-gray-700 leading-relaxed text-[15px]">
            <p className="text-lg font-semibold text-gray-900">
              Fireline Email Co. wasn't built by a marketing agency.
            </p>
            <p>
              It was built by someone who's stood on both sides of the clipboard.
            </p>
            <p>
              Before running businesses full-time, I served as a paid on-call firefighter. I've seen firsthand what happens when systems fail — and what it takes to prevent that from happening.
            </p>
            <p>
              Today, I'm a small business owner who owns and operates a commercial building. I hire and work with fire protection companies. I receive the inspection reports. I sign the invoices. I rely on those systems to protect my people and my property.
            </p>
            <p className="font-semibold text-gray-900">
              That gives me a perspective most marketing companies simply don't have.
            </p>

            {/* Values list */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
              <p className="text-sm font-semibold text-gray-900 mb-4">I understand:</p>
              <ul className="space-y-3">
                {values.map((v) => (
                  <li key={v.text} className="flex items-center gap-3 text-sm text-gray-700">
                    <span className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-full text-red-600 shrink-0">
                      <i className={`${v.icon} text-sm`} />
                    </span>
                    {v.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-l-4 border-red-600 pl-5 space-y-2">
              <p className="font-semibold text-gray-900">Fireline Email Co. exists because I saw a gap.</p>
              <p>
                Great fire protection companies do critical work — but most of them are invisible between inspections.
              </p>
              <p className="font-semibold text-gray-900">I help fix that.</p>
            </div>

            <p>
              Not with hype.<br />
              Not with gimmicks.<br />
              But with steady, intelligent communication that reflects the seriousness of the work you do.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-red-400 text-sm font-semibold uppercase tracking-widest mb-4">Ready to get started?</p>
          <h2 className="text-3xl font-bold text-white mb-6">
            Let's put your company in front of your customers — every single day, in a helpful, non-annoying way.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="bg-red-600 text-white px-8 py-4 rounded-md text-base font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book a Strategy Call
            </Link>
            <Link
              to="/"
              className="border border-gray-600 text-gray-300 px-8 py-4 rounded-md text-base font-semibold hover:border-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              See Pricing
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 py-8 text-center">
        <p className="text-gray-400 text-sm">
          &copy; {new Date().getFullYear()} Fireline Email Co. All rights reserved.
        </p>
      </div>
    </div>
  );
}
